/**
 * promptLibraryLive — the parts of the prompt library that change between
 * builds: published reviews, recorded runs, and revocations. (CO 613, CO 614)
 *
 * /prompts is generated at build time from the canon (CO 530), which is right
 * for a prompt's text and wrong for these: a review published this morning or
 * a prompt revoked ten minutes ago must not wait for a deploy. The page reads
 * them from the public library tool and lays them over the built cards by id.
 * If the read fails the page is the build-time page, unchanged.
 */

const GATEWAY_URL =
  import.meta.env.VITE_FEDERATION_GATEWAY_URL ||
  "https://mcp.comos-gateway.com";

/** A listed prompt written outside ComOS: not in the build, so the live read carries all of it. */
export interface CommunityPrompt {
  id: string;
  title: string;
  description: string | null;
  author: string | null;
  sha256: string;
  admitted: string | null;
  /** The bench run it was listed on (CO 615). */
  tested: { release: string; calls: number; at: string } | null;
  body: string;
  manifest: {
    tools: string[];
    scopes: string[];
    step_up: string[];
    effect: string;
  };
}

export interface LiveCollection {
  key: string;
  title: string;
  description: string;
  ids: string[];
}

export interface LiveLibrary {
  state: Map<string, LivePromptState>;
  community: CommunityPrompt[];
  collections: LiveCollection[];
}

export interface PublicReview {
  review_id: string;
  rating: number;
  title: string | null;
  body: string | null;
  reviewer: string;
  verified_run: boolean;
  created_at: string;
}

export interface LivePromptState {
  sha256: string | null;
  reviews?: { count: number; average: number; verified_runs: number };
  runs?: { total: number; clean: number; last_at: string | null };
  revoked?: { reason: string; at: string };
  stale?: boolean;
}

async function callPublicTool<T>(tool: string, args: unknown): Promise<T> {
  const res = await fetch(`${GATEWAY_URL}/tools/${tool}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`${tool} read failed: ${res.status}`);
  const wire = (await res.json()) as {
    result?: { content?: Array<{ text?: string }> };
  };
  const text = wire.result?.content?.[0]?.text;
  if (typeof text !== "string") throw new Error(`${tool} read: no content`);
  return JSON.parse(text) as T;
}

type WireEntry = LivePromptState & {
  id: string | null;
  title?: string;
  description?: string | null;
  source?: string;
  author?: string | null;
  admitted?: string | null;
  tested?: CommunityPrompt["tested"];
  manifest?: CommunityPrompt["manifest"] | null;
  body?: { kind?: string; text?: string };
};

/** The live library: per-prompt state, listed outside prompts, collections. Throws on any failure. */
export async function fetchLiveLibrary(): Promise<LiveLibrary> {
  const body = await callPublicTool<{
    ok?: boolean;
    groups?: Array<{ entries?: WireEntry[] }>;
    collections?: LiveCollection[];
  }>("introspection_prompt_library", { include_body: true });
  if (body.ok !== true || !Array.isArray(body.groups)) {
    throw new Error("prompt library read: not ok");
  }
  const state = new Map<string, LivePromptState>();
  const community: CommunityPrompt[] = [];
  for (const g of body.groups) {
    for (const e of g.entries ?? []) {
      if (!e.id) continue;
      state.set(e.id, {
        sha256: e.sha256 ?? null,
        reviews: e.reviews,
        runs: e.runs,
        revoked: e.revoked,
        stale: e.stale,
      });
      if (
        e.source === "submission" &&
        e.sha256 &&
        e.manifest &&
        e.body?.kind === "library-prompt" &&
        typeof e.body.text === "string" &&
        typeof e.title === "string"
      ) {
        community.push({
          id: e.id,
          title: e.title,
          description: e.description ?? null,
          author: e.author ?? null,
          sha256: e.sha256,
          admitted: e.admitted ?? null,
          tested: e.tested ?? null,
          body: e.body.text,
          manifest: e.manifest,
        });
      }
    }
  }
  return { state, community, collections: Array.isArray(body.collections) ? body.collections : [] };
}

/** The published reviews of one prompt's current version. Throws on any failure. */
export async function fetchPromptReviews(id: string): Promise<PublicReview[]> {
  const body = await callPublicTool<{ ok?: boolean; reviews?: PublicReview[] }>(
    "federation_prompt_reviews",
    { id },
  );
  if (body.ok !== true || !Array.isArray(body.reviews)) throw new Error("reviews read: not ok");
  return body.reviews;
}
